import { Injectable } from '@angular/core'
import { Observable } from 'rxjs'
import { first, map } from 'rxjs/operators'

import { Persona } from 'app/models'

import { ApiService } from './api.service'
import { Mapper } from './mapper.service'
import { Store } from './store'

@Injectable({
  providedIn: 'root'
})
export class UserPersonaService extends Store<Persona[]> {

  constructor(private api: ApiService) {
    super([])
  }

  public userId: string
  private readonly personaRefreshVersions = new Map<number, number>()

  /**
   * finds all personas for userId and sets current userId
   */
  public findCurrent() {
    this.reset()
    this.getPersonas()
      .pipe(first())
      .subscribe(
        (data: any) => {
          const personas = Mapper.asArray<Persona>(data, Persona)
          this.setCurrent(personas)
        }
      )
  }

  public getPersonas(): Observable<any> {
    const now = Date.now()
    return this.api.get(`v3/households/profiles/mine?access=manager&_v=${now}&per_page=200`)
      .pipe(
        first(),
        map(response => response.result.profiles.map(profile => {
          profile.persona.type = profile.relationship
          return profile.persona
        }))
      )
  }

  public reset(): void {
    this.personaRefreshVersions.forEach((version, personaId) => {
      this.personaRefreshVersions.set(personaId, version + 1)
    })
    super.reset()
  }

  /** Load the complete selected profile before checking purchase requirements. */
  public async refreshPersona(personaId: number, initializedPersonas: Persona[] = []): Promise<Persona> {
    const version = (this.personaRefreshVersions.get(personaId) || 0) + 1
    this.personaRefreshVersions.set(personaId, version)
    const response = await this.api.get(`v3/households/profiles/${personaId}/demographics?_v=${Date.now()}`)
      .pipe(first()).toPromise()
    // A later verification owns this profile, even if its request is still pending or failed.
    if (this.personaRefreshVersions.get(personaId) !== version) {
      throw new Error('Purchase profile refresh was superseded')
    }
    const profile = response && response.result
    const demographics = profile && profile.demographics
    // Omitted demographics mean an incomplete response, not unanswered questions.
    if (!profile || !profile.persona || profile.persona.id !== personaId || !profile.relationship ||
        !demographics || demographics.persona_id !== personaId || !Array.isArray(demographics.ethnicities) ||
        !Object.prototype.hasOwnProperty.call(demographics, 'disability') ||
        !Object.prototype.hasOwnProperty.call(demographics, 'military_status')) {
      throw new Error('Unable to load the complete purchase profile')
    }
    const persona = new Persona({
      ...profile.persona,
      ethnicities: demographics.ethnicities,
      disability: demographics.disability,
      military_status: demographics.military_status,
      type: profile.relationship
    })
    const current = this.subject.value
    const cached = current.find(item => item.id === personaId)
    const initialized = initializedPersonas.find(item => item.id === personaId)
    persona.memberships = initialized && initialized.memberships && initialized.memberships.length
      ? initialized.memberships : cached && cached.memberships || []
    this.setCurrent(current.some(item => item.id === personaId)
      ? current.map(item => item.id === personaId ? persona : item)
      : [...current, persona])
    return persona
  }

  public setCurrent(personas: Persona[]): void {
    this.subject.next(personas)
  }

  public reloadCurrent(): void {
    this.findCurrent()
  }

  /**
   * Update a sub-profile, persona / child for the current user
   * @param persona the persona to update
   */
  public updatePersona(persona: Persona): Observable<any> {
    const data: any = {
      first_name: persona.first_name,
      nickname: persona.nickname,
      middle_name: persona.middle_name,
      last_name: persona.last_name,
      suffix: persona.suffix
    }
    if (persona.gender) data.gender = persona.gender
    if (persona.phone_numbers) data.phone_numbers = persona.phone_numbers
    if (persona.date_of_birth) data.date_of_birth = persona.date_of_birth // @todo format this? timezoneify?
    if (persona.addresses) data.addresses = persona.addresses
    if (persona.graduation_year) data.graduation_year = persona.graduation_year
    if (persona.ethnicities) data.ethnicities = persona.ethnicities
    if (persona.disability) data.disability = persona.disability
    if (persona.military_status) data.military_status = persona.military_status

    return this.api.put(`personas/${persona.id}`, data)
  }

  /**
   * updates a persona record
   * to be used in updatePersona handler
   * @param persona the exsting persona from current to update
   * @param newPersona the new persona
   */
  public reloadPersona(persona: Persona, newPersona: Persona): void {
    // @todo find persona in current
    //  swap it out with newPersona
    //  this.subject.next(...)
    const currentPersonas = this.subject.value
    const newPersonas = currentPersonas.filter(curPersona => curPersona.id !== persona.id)
    this.subject.next(newPersonas.concat([newPersona]))
  }
}
